import { Table } from '@tanstack/react-table';
import {
    MdKeyboardArrowLeft,
    MdKeyboardArrowRight,
    MdKeyboardDoubleArrowLeft,
    MdKeyboardDoubleArrowRight
} from 'react-icons/md';

import Select from '@/components/inputs/Select';
import { Option } from '@/constants/general';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectTablePagination, setTablePagination } from '@/state/reducers/tableSettingSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import { useEffect, useMemo } from 'react';

export type TablePaginationProps<T extends object> = {
    manualPagination?: {
        pageSize: number;
    };
    tableInstance: Table<T>;
    setManualPagination?: (pagination: { pageSize: number }) => void;
    getPageSize?: (pageSize: number) => void;
    className?: string;
};

const TablePagination = <T extends object>({
    manualPagination,
    tableInstance,
    setManualPagination,
    getPageSize,
    className = 'flex justify-end items-center gap-2 flex-wrap'
}: TablePaginationProps<T>) => {
    const dispatch = useAppDispatch();
    const pagination = useAppSelector(selectTablePagination);

    const lang = useLanguage();

    const pageSizeOptions: Option[] = useMemo(() => {
        return [
            { label: `${lang.blotters.show} 10`, value: 10 },
            { label: `${lang.blotters.show} 20`, value: 20 },
            { label: `${lang.blotters.show} 50`, value: 50 },
            { label: `${lang.blotters.show} 100`, value: 100 }
        ];
    }, [lang]);

    useEffect(() => {
        const pageSize = manualPagination?.pageSize || pagination.pageSize || pageSizeOptions[0].value;
        tableInstance.setPageSize(pageSize);
    }, [tableInstance, manualPagination, pagination]);

    return (
        <div className={className}>
            <button
                className="rounded-md cursor-pointer p-2 hover:bg-brand-primary"
                onClick={() => tableInstance.setPageIndex(0)}
                disabled={!tableInstance.getCanPreviousPage()}>
                <MdKeyboardDoubleArrowLeft className="w-4 h-4" />
            </button>
            <button
                className="rounded-md cursor-pointer p-2 hover:bg-brand-primary"
                onClick={() => tableInstance.previousPage()}
                disabled={!tableInstance.getCanPreviousPage()}>
                <MdKeyboardArrowLeft className="w-4 h-4" />
            </button>
            <button
                className="rounded-md cursor-pointer p-2 hover:bg-brand-primary"
                onClick={() => tableInstance.nextPage()}
                disabled={!tableInstance.getCanNextPage()}>
                <MdKeyboardArrowRight className="w-4 h-4" />
            </button>
            <button
                className="rounded-md cursor-pointer p-2 hover:bg-brand-primary"
                onClick={() => tableInstance.setPageIndex(tableInstance.getPageCount() - 1)}
                disabled={!tableInstance.getCanNextPage()}>
                <MdKeyboardDoubleArrowRight className="w-4 h-4" />
            </button>
            {!!tableInstance.getPageCount() && (
                <span className="flex items-center gap-1 text-xs">
                    {/* TBA - Translations */}
                    <div>Page</div>
                    {tableInstance.getState().pagination.pageIndex + 1} of {tableInstance.getPageCount()}
                </span>
            )}
            <span>
                <Select
                    selected={pageSizeOptions.find(
                        (option) => option.value === tableInstance.getState().pagination.pageSize
                    )}
                    onChange={(e) => {
                        tableInstance.setPageSize(e.value);
                        if (manualPagination && setManualPagination) {
                            setManualPagination({ pageSize: e.value });
                        } else {
                            dispatch(setTablePagination(e.value));
                        }
                        getPageSize && getPageSize(e.value);
                    }}
                    options={pageSizeOptions}
                    className="text-xs"
                    // menuClassName="bottom-10 mb-2"
                    // disableSorting={true}
                />
            </span>
        </div>
    );
};

export default TablePagination;
